import {
  TbAlertTriangle,
  TbCaretDown,
  TbCaretUp,
  TbCircleCheckFilled,
  TbFileText,
  TbLoader2,
  TbTrash,
  TbX,
} from "solid-icons/tb";
import { useAppUI } from "./AppUIContext";
import { StButton } from "@storytell/ui/buttons";
import { useWire } from "~/wire";
import { createSignal, For, Match, onMount, Show, Switch } from "solid-js";
import {
  type ColumnDef,
  createSolidTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Header,
  type SortingState,
} from "@tanstack/solid-table";
import type { AssetRecord } from "~/stores/sync/syncAssets";
import { lifecycleStateToShort } from "~/components/utils/lifecycleStateMappers";
import { Checkbox } from "@kobalte/core/checkbox";
import { TbCheck } from "solid-icons/tb";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useAssetClient, useCallOptions, withBearerToken } from "@storytell/cp-client";
import { AssetLifecycleState } from "@storytell/cp-client/src/gen/assets/v3/asset_pb";
dayjs.extend(localizedFormat);

export const NavigationAssetsPane = () => {
  const ui = useAppUI();

  return (
    <div class="flex flex-col h-full px-4 py-3 space-y-4">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-medium">Assets</h2>
        <StButton onClick={() => ui.actions.closeSecondaryPane()} icon={TbX} />
      </div>

      <AssetsTable />
    </div>
  );
};

const AssetsTable = () => {
  const wire = useWire();

  // Search state
  const [search, setSearch] = createSignal("");

  // Default sorting: by scope column, descending (in-scope first)
  const [sorting, setSorting] = createSignal<SortingState>([]);

  // Bulk selection state
  const [selected, setSelected] = createSignal<Record<string, boolean>>({});

  // Helper to get all visible asset IDs (first 10 rows)
  const allSelected = () => assets().every((a) => selected()[a?.id ?? ""]);
  const someSelected = () => assets().some((a) => selected()[a?.id ?? ""]);

  const [deleting, setDeleting] = createSignal<Record<string, boolean>>({});

  const onBulkDelete = async () => {
    if (!window.confirm("Are you sure you want to bulk delete these assets?")) return;

    const ids = Object.entries(selected())
      .filter(([_, value]) => value)
      .map(([id]) => id);

    if (ids.length === 0) return;

    const assetClient = useAssetClient();
    setDeleting((prev) => ({ ...prev, ...selected() }));

    try {
      const callOptions = useCallOptions(withBearerToken(wire.auth.token()));
      const result = await assetClient.deleteAssets({ assetIds: ids }, callOptions);

      if (result.isErr()) {
        console.error(result.error);
        throw result.error;
      }

      if (result.isOk()) {
        for (const asset of result.value.assets) {
          wire.sync.assets.set(asset.data.id, {
            ...asset,
            id: asset.data.id,
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      const deleted = ids.reduce(
        (acc, id) => {
          acc[id] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      );

      setDeleting((prev) => ({ ...prev, ...deleted }));
    }
  };

  const onSingleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this asset?")) return;

    const assetClient = useAssetClient();

    setDeleting((prev) => ({ ...prev, [id]: true }));
    try {
      const callOptions = useCallOptions(withBearerToken(wire.auth.token()));
      const result = await assetClient.deleteAsset(
        {
          assetId: id,
        },
        callOptions,
      );
      if (result.isErr()) {
        console.error(result.error);
        throw result.error;
      }

      if (result.isOk()) {
        wire.sync.assets.set(result.value.asset.data.id, {
          ...result.value.asset,
          id: result.value.asset.data.id,
        });
        console.log("deleted", result.value.asset);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDeleting((prev) => ({ ...prev, [id]: false }));
      setSelected((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  // Toggle all visible checkboxes
  function toggleAll(checked: boolean) {
    const ids = assets().map((a) => a?.id);
    setSelected((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = checked;
      });
      return next;
    });
  }

  // Sorting based on initial scope to bring the in-scope assets to the top
  // but also prevent them from shifting around as the user adds/removes assets
  const assets = () => {
    if (search()) {
      return wire.sync.assets.fuzzy
        .search(search())
        .map((result) => wire.sync.assets.get(result.item.id))
        .filter(Boolean) as AssetRecord[];
    }
    return wire.sync.assets.getAll();
  };

  // Table columns definition
  const columns: ColumnDef<AssetRecord>[] = [
    {
      id: "bulk-select",
      header: () => (
        <Checkbox
          checked={allSelected()}
          indeterminate={someSelected() && !allSelected()}
          aria-label={allSelected() ? "Deselect all" : "Select all"}
          onChange={toggleAll}
          class="flex items-center justify-center"
        >
          <Checkbox.Input />
          <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
            <Show when={allSelected() || (someSelected() && !allSelected())}>
              <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
            </Show>
          </Checkbox.Control>
        </Checkbox>
      ),
      cell: (info) => {
        const id = () => info.row.original.id;
        const checked = () => !!selected()[id()];
        return (
          <Checkbox
            checked={checked()}
            aria-label={checked() ? "Deselect asset" : "Select asset"}
            onChange={(value) => {
              setSelected((prev) => ({ ...prev, [id()]: value }));
            }}
            class="flex items-center justify-center"
          >
            <Checkbox.Input />
            <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
              <Show when={checked()}>
                <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
              </Show>
            </Checkbox.Control>
          </Checkbox>
        );
      },
      footer: (props) => props.column.id,
      enableSorting: false,
    },
    {
      header: "Name",
      accessorFn: (row) => row.data.originalFilename,
      cell: (info) => <span class="whitespace-nowrap font-medium text-primary-text">{String(info.getValue())}</span>,
      footer: (props) => props.column.id,
    },
    {
      header: "File Size",
      accessorFn: (row) => row.data.fileSize,
      cell: (info) => formatFileSize(info.getValue() as number | bigint),
      footer: (props) => props.column.id,
    },
    {
      header: "Last Updated",
      accessorFn: (row) => row.data.auditMetadata?.modified?.occurredAt,
      cell: (info) => {
        const date = () => {
          const ts = info.getValue() as { seconds?: number; nanos?: number } | undefined;
          if (!ts || typeof ts !== "object" || ts.seconds == null) return "-";
          // Convert protobuf Timestamp to JS Date
          const date = dayjs.unix(Number(ts.seconds));
          return date.format("LLL");
        };
        return <span class="text-xs">{date()}</span>;
      },
      footer: (props) => props.column.id,
    },
    {
      header: "Status",
      cell: (info) => {
        const value = () => lifecycleStateToShort(info.row.original.data.lifecycleState);
        return (
          <span class="flex items-center gap-1 capitalize text-xs">
            <Switch>
              <Match when={value() === "uploading"}>
                <TbLoader2 class="w-3 h-3 animate-spin group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
              </Match>
              <Match when={value() === "error"}>
                <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform" />
              </Match>
              <Match when={value() === "processing"}>
                <div class="relative">
                  <TbFileText class="w-3 h-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
                  <div class="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-brand rounded-full animate-pulse" />
                </div>
              </Match>
              <Match when={value() === "ready"}>
                <TbCircleCheckFilled class="w-3 h-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
              </Match>
            </Switch>
            {value()}
          </span>
        );
      },
      footer: (props) => props.column.id,
    },

    {
      header: " ",
      accessorFn: (row) => row.data.id,
      cell: (info) => {
        const id = () => String(info.getValue());
        return (
          <StButton
            class="bg-tile hover:bg-tile-hover"
            onClick={() => onSingleDelete(id())}
            icon={TbTrash}
            loading={deleting()[id()]}
          />
        );
      },
      footer: (props) => props.column.id,
    },
  ];

  // Create the table instance
  const table = createSolidTable({
    get data() {
      return assets();
    },
    columns,
    state: {
      get sorting() {
        return sorting();
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  // Keyboard handler for sorting
  function handleHeaderKeyDown(e: KeyboardEvent, header: Header<AssetRecord, unknown>) {
    if (header.column.getCanSort() && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      const handler = header.column.getToggleSortingHandler();
      if (handler) handler(e);
    }
  }

  let inputRef!: HTMLInputElement;
  onMount(() => {
    inputRef.focus();
  });

  return (
    <>
      <div class="mb-3 flex items-center gap-4">
        <Show when={Object.values(selected()).filter(Boolean).length > 1}>
          <StButton
            noColor
            class="shrink-0 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 border-red-300 dark:border-red-700 hover:border-red-500 dark:hover:border-red-500"
            icon={TbTrash}
            onClick={onBulkDelete}
            loading={Object.values(deleting()).some(Boolean)}
          >
            Bulk delete {Object.values(selected()).filter(Boolean).length} assets
          </StButton>
        </Show>
        <input
          type="text"
          value={search()}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder="Search assets..."
          class="w-full px-3 py-2 border border-primary-border rounded bg-tile text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          aria-label="Search assets"
          ref={inputRef}
        />
      </div>

      <div class="rounded-lg border border-primary-border overflow-auto flex-auto mb-2">
        <table class="min-w-full max-w-full text-sm overflow-auto">
          <thead class="sticky top-0 bg-canvas border-b border-primary-border">
            <For each={table.getHeaderGroups()}>
              {(headerGroup) => (
                <tr>
                  <For each={headerGroup.headers}>
                    {(header) => (
                      <th
                        colSpan={header.colSpan}
                        class="text-xs px-2 py-2 font-medium text-left text-primary-text whitespace-nowrap"
                      >
                        <Show when={!header.isPlaceholder}>
                          <div
                            class={
                              header.column.getCanSort()
                                ? "cursor-pointer select-none flex items-center gap-1"
                                : undefined
                            }
                            tabIndex={header.column.getCanSort() ? 0 : undefined}
                            role={header.column.getCanSort() ? "button" : undefined}
                            aria-pressed={header.column.getIsSorted() ? "true" : "false"}
                            onClick={header.column.getToggleSortingHandler()}
                            onKeyDown={(e) => handleHeaderKeyDown(e as KeyboardEvent, header)}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <Show when={header.column.getIsSorted() === "asc"}>
                              <TbCaretUp class="inline-block w-4 h-4 text-brand" />
                            </Show>
                            <Show when={header.column.getIsSorted() === "desc"}>
                              <TbCaretDown class="inline-block w-4 h-4 text-brand" />
                            </Show>
                          </div>
                        </Show>
                      </th>
                    )}
                  </For>
                </tr>
              )}
            </For>
          </thead>
          <tbody>
            <Show
              when={table.getRowModel().rows.length > 0}
              fallback={
                <tr>
                  <td colSpan={columns.length} class="text-center p-4 text-muted-text">
                    No assets found
                  </td>
                </tr>
              }
            >
              <For each={table.getRowModel().rows}>
                {(row) => (
                  <tr class="hover:bg-tile-hover transition-colors">
                    <For each={row.getVisibleCells()}>
                      {(cell) => (
                        <td class="px-3 py-2 [&:not:last-child]:border-b border-muted-border text-secondary-text">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </Show>
          </tbody>
        </table>
      </div>
    </>
  );
};

// Format file size to human readable string
function formatFileSize(bytes: bigint | number) {
  let size = typeof bytes === "bigint" ? Number(bytes) : typeof bytes === "number" ? bytes : 0;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}
